import * as PIXI from 'pixi.js-legacy';
import {
  Animation,
  Rectangle,
  Polygon,
  Circle,
  Bitmap,
  Text,
  Spritesheet,
  Container,
  ScrollArea,
  HTMLArea,
  InputTextField,
  ModifiedEventBoundary,
} from './pixiComponentClasses';
import addFreecodeGraphics from './addFreecodeGraphics';
import getFreecodeLazyImageData from './getFreecodeLazyImageData';

PIXI.Container.defaultSortableChildren = true;
PIXI.HTMLText.defaultMaxHeight = 4048;

// 64bit floating point value that directly precedes 1.0.
const BELOW_ONE = 0.999999940395355224609375;

const SRC_REGEX = /src *= *"(.+?)"/gi;

// Get a range of numbers [0, length)
const range = (length) => Array.from({ length }, (_, i) => i);

// Linear interpolation.
const lerp = (start, end, time) => start + time * (end - start);

// Get a numbers fraction
const fract = (num) => num - Math.floor(num);

const splitRGB = (colorInt) => [colorInt & 0xff, (colorInt >>> 8) & 0xff, (colorInt >>> 16) & 0xff];

const joinRGB = (r, g, b) => Math.trunc(r) | (Math.trunc(g) << 8) | (Math.trunc(b) << 16);

// Decorator that splits a color into components and applies the interpolation to each.
const colorInterpolator = (interpolate) => (start, end, time) => {
  const startRGB = splitRGB(start);
  const endRGB = splitRGB(end);
  return joinRGB(
    interpolate(startRGB[0], endRGB[0], time),
    interpolate(startRGB[1], endRGB[1], time),
    interpolate(startRGB[2], endRGB[2], time)
  );
};

export function getPixiComponent() {
  if (!window.PixiComponent) window.PixiComponent = new PixiComponent();
  return window.PixiComponent;
}

export function getNewPixiComponent() {
  return new PixiComponent();
}

function PixiComponent() {
  const pixiComponent = this;

  this.stage = new PIXI.Container();
  this.levelScale = 1.0;
  this.ticker = PIXI.Ticker.shared;
  this.app = null;
  this.preloadedImages = {};
  this.currentLevel = 0;
  this.currentStep = 0;
  this.pixiObjects = {};
  this.background = '';
  this.isDarkMode = false;

  // variables for toggle, drag&Drop, and scroll state
  this.toggleGroups = {};
  this.toggleStates = {};
  this.scrollXStates = {};
  this.scrollYStates = {};
  this.inputTextFieldStates = {};
  this.childToReRender = null;
  this.keyboardEventListeners = new Map();

  this.initialize = function (options, tickerFunc) {
    if (options.isDarkMode !== undefined) {
      this.isDarkMode = options.isDarkMode;
    }
    if (options.backgroundColor !== undefined) {
      this.background = options.backgroundColor;
      options.backgroundColor = stringColorToNumberColor(options.backgroundColor);
    }

    if (this.app !== null) {
      this.stage = new PIXI.Container();
      this.stage.sortableChildren = true;
    }

    this.app = new PIXI.Application(options);
    this.app.stage = this.stage;
    this.app.stage.sortableChildren = true;
    this.app.ticker = this.ticker;
    this.app.renderer.events.rootBoundary = new ModifiedEventBoundary(null);

    this.ticker.add(tickerFunc);
    this.app.start();

    this.levelScale = 1.0;
  };

  this.setTweenAndRenderStep = function (subStep) {
    this.setTween(subStep);
    this.app.renderer.render(this.stage);
  };

  this.renderStep = function () {
    this.app.renderer.render(this.stage);
  };

  this.setTween = function (t) {
    this.drawLevel(this.currentStep, t);
  };

  this.setDarkMode = async function (isDarkMode, levels) {
    if (this.isDarkMode === isDarkMode) return;
    this.isDarkMode = isDarkMode;
    this.setBackgroundColor(this.background);
    await this.preprocessLevelOutput(levels, this._pixelsPerUnit);
    this.setLevelAndStep(this.currentLevel, this.currentStep);
    this.setTween(1);
  };

  this.setBackgroundColor = function (backgroundColor) {
    this.background = backgroundColor || '';
    const parts = this.background.split('|', 2);
    const color = this.isDarkMode && parts.length > 1 ? parts[1] : parts[0];
    this.app.renderer.background.color = stringColorToNumberColor(color);
  };

  this.addPreloadedImage = function (loadedImage) {
    const base = new PIXI.BaseTexture(loadedImage);
    loadedImage.tex = new PIXI.Texture(base);
    this.preloadedImages[loadedImage.name] = loadedImage;
  };

  this.getPreloadedImage = function (name) {
    const parts = name.split('|', 2);
    const part = this.isDarkMode && parts.length > 1 ? parts[1] : parts[0];
    if (!this.preloadedImages || !this.preloadedImages[part]) {
      const error = 'There is no image named "' + part + '"';
      console.error(error);
      if (!this.imageAlerts) this.imageAlerts = {};
      if (!this.imageAlerts[part]) {
        this.imageAlerts[part] = true;
        alert(error);
      }
      this.addMissingTexture(name);
    }
    return this.preloadedImages[part];
  };

  this.addMissingTexture = function (name) {
    // Adds an image that is a single pixel with magenta color
    let canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    canvas.name = name;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'magenta';
    ctx.fillRect(0, 0, 1, 1);
    this.addPreloadedImage(canvas);
    canvas.remove();
  };

  this.setNormalScale = function () {
    this.setScaleAndResolution(1.0);
  };

  this.setDoubleScale = function () {
    this.setScaleAndResolution(2.0);
  };

  this.setScaleAndResolution = function (scale, resolution) {
    if (typeof scale === 'number') this.levelScale = scale;
    if (typeof resolution === 'number') this._pixelsPerUnit = resolution;
    this.stage.scale.set(this._pixelsPerUnit * this.levelScale);
  };

  this.clearPixiObjects = function () {
    for (const keyName in this.pixiObjects) {
      this.pixiObjects[keyName].destroy();
    }
    this.pixiObjects = {};
  };

  this.loadLevelPixiObjects = function (levelIndex) {
    this.clearPixiObjects();
    const startTime = Date.now();
    this.pixiObjects = this._createLevelObjects(this.processedLevels[levelIndex]);
    const endTime = Date.now();
    console.info('Pixi objects construction time: ' + (endTime - startTime).toString() + 'ms');
  };

  this.preprocessLevelOutput = async function (levels, pixelsPerUnit) {
    await this.getPredefinedGraphics(levels);
    this.setScaleAndResolution(null, pixelsPerUnit);
    const a = Date.now();
    this.clearPixiObjects();
    this.processedLevels = levels.map(this._preprocessLevel);
    this.levelStepCounts = levels.map((level) => level.steps.length);
    const b = Date.now();
    console.info('Preprocess time: ' + (b - a).toString() + 'ms');
    this.loadLevelPixiObjects(this.currentLevel);
    const c = Date.now();
    console.info('Total time: ' + (c - a).toString() + 'ms');
  };

  this.getPredefinedGraphics = async function (levels) {
    let promises = [];
    for (const level of levels) {
      for (const step of level.steps) {
        for (const graphic of step.graphics) {
          if (!graphic.updates.image || this.preloadedImages[graphic.updates.image]) continue;
          const images = graphic.updates.image.split('|', 2);
          for (const image of images) {
            if (this.preloadedImages[image]) continue;
            const imageData = getFreecodeLazyImageData(image);
            if (!imageData) continue;
            promises.push(addFreecodeGraphics(imageData, this));
          }
        }
      }
    }
    await Promise.all(promises);
  };

  function onGrab(pixiObject) {
    if (pixiObject.dragEffect) pixiComponent.applyEffect(pixiObject, pixiObject.dragEffect);
    if (pixiObject.dragTargets) pixiComponent.toggleTargets(pixiObject.dragTargets, true);
    if (pixiObject.children) pixiObject.children.forEach(onGrab);
  }

  function onRelease(pixiObject) {
    if (pixiObject.dragEffect) pixiComponent.revertEffect(pixiObject);
    if (pixiObject.dragTargets) pixiComponent.toggleTargets(pixiObject.dragTargets, false);
    if (pixiObject.children) pixiObject.children.forEach(onRelease);
  }

  function setMouseEventsForDrag(pixiObject, pixelsPerUnit) {
    pixiObject.onpointerdown = function (e) {
      // Drag event started
      onGrab(pixiObject);

      // Store pointer offset from center of object
      this.pointerOffsetX = e.data.global.x / pixelsPerUnit - pixiObject.x;
      this.pointerOffsetY = e.data.global.y / pixelsPerUnit - pixiObject.y;

      this.dragging = true;
      this.cursor = 'grabbing';

      if (this.parent) {
        let parent = this.parent;
        parent.removeChild(this);
        parent.addChild(this);
      }
    };

    const calculateNewCoordination = (newCoord, size, center, pivot, dragZoneSize) => {
      // calculate new coordination based on dragZone limitation
      const min = -dragZoneSize / 2 + size / 2 - center + pivot;
      const max = dragZoneSize / 2 - size / 2 - center + pivot;

      if (newCoord > max) return max;
      else if (newCoord < min) return min;
      return newCoord;
    };

    pixiObject.onglobalpointermove = function (e) {
      // Pointer move while dragging
      if (pixiObject.dragging) {
        let centerX = 0;
        let centerY = 0;
        if (pixiObject.polygonCenter) {
          centerX = pixiObject.polygonCenter[0];
          centerY = pixiObject.polygonCenter[1];
        }

        pixiObject.x = calculateNewCoordination(
          e.data.global.x / pixelsPerUnit - this.pointerOffsetX, // new x
          pixiObject.width,
          centerX,
          pixiObject.pivot.x,
          pixiObject.parent.width
        );

        pixiObject.y = calculateNewCoordination(
          e.data.global.y / pixelsPerUnit - this.pointerOffsetY, // new y
          pixiObject.height,
          centerY,
          pixiObject.pivot.y,
          pixiObject.parent.height
        );
      }
    };

    pixiObject.onpointerup = pixiObject.onpointerupoutside = function (e) {
      // On dropped event
      this.dragging = false;
      onRelease(pixiObject);
      onPointerUp(pixiObject.name, { x: pixiObject.x, y: pixiObject.y });
    };
  }

  function addUpdateToAnimation(stepIndex, animation, updates) {
    for (const key of animation.trackNames) {
      if (updates[key] !== undefined) {
        let sequence = updates[key];
        if (!(sequence instanceof Array)) {
          sequence = [[sequence, 1.0]];
        }
        if (key === 'color') {
          sequence = sequence.map((k) => [stringColorToNumberColor(k[0]), k[1]]);
        }
        sequence.forEach(function (keyframe) {
          const value = keyframe[0];
          const time = stepIndex + Math.min(BELOW_ONE, keyframe[1] || 0.0);
          const hasKeyframes = animation[key].getKeyframeCount() > 0;
          const sameValueAsPrevious = animation[key].getLastKeyframeValue() === value;
          const previousIsSameStep = fract(time) === fract(animation[key].getLastKeyframeTime());
          const skip = !previousIsSameStep && sequence.length === 1 && sameValueAsPrevious;
          if (!hasKeyframes || !skip) {
            animation[key].addKeyframe(time, value);
          }
        });
      }
    }
  }

  const interpolatedTrackNames = [
    'x',
    'y',
    'scaleX',
    'scaleY',
    'zIndex',
    'color',
    'alpha',
    'rotation',
    'pivotX',
    'pivotY',
  ];
  const commonAnimationTrackNames = interpolatedTrackNames.concat([
    'parent',
    'hoverEffect',
    'actionEffect',
    'dragEffect',
    'hoverTargets',
    'clickTargets',
    'interactClickTargets',
    'dragTargets',
    'draggable',
    'followBottom',
    'isStandalone',
    'keyList',
  ]);
  const staticProperties = [
    'points',
    'callbacks',
    'frames',
    'image',
    'width',
    'height',
    'radius',
    'edge',
    'polygonCenter',
    'thickness',
    'toggleByDefault',
    'toggleGroup',
  ];
  const textTrackNames = commonAnimationTrackNames.concat(['text', 'size', 'textAlign', 'isHTML']);
  const textFieldTrackNames = commonAnimationTrackNames.concat([
    'text',
    'text_color',
    'default_text_color',
    'border_color',
    'default_text',
    'active_background_color',
  ]);

  this._preprocessLevel = function (level) {
    const animations = {};
    for (let stepIndex = 0; stepIndex < level.steps.length; stepIndex++) {
      const step = level.steps[stepIndex];
      for (const update of step.graphics) {
        const id = update.name;
        if (update.type !== undefined) {
          // Only first update package for a gfx object will have this.
          let trackNames;
          switch (update.type) {
            case 'text':
            case 'html':
              trackNames = textTrackNames;
              break;
            case 'inputtextfield':
              trackNames = textFieldTrackNames;
              break;
            default:
              trackNames = commonAnimationTrackNames;
          }
          animations[id] = new Animation(update.type, id, stepIndex, trackNames);
          for (let i = 0; i < staticProperties.length; i++) {
            animations[id][staticProperties[i]] = update.updates[staticProperties[i]];
          }
          if ('spritesheet' === update.type) {
            const sheetImage = pixiComponent.getPreloadedImage(update.updates.image);
            const frameCount =
              !sheetImage.sheetData || !sheetImage.sheetData.animations
                ? 1
                : sheetImage.sheetData.animations[update.updates.animation].length;
            animations[id].frames = range(frameCount).map((i) => [i, (i + 1) / (frameCount + 1)]);
          }
          if (update.updates.parent === undefined) {
            update.updates.parent = 'root'; // TODO: Add parent to all init updates on server.
          }
        }
        if (update.updates.destroy) {
          // TODO: Error check. Here or validate in python?
          // Could potentially be set multiple times for the same object.
          animations[id].destructionStep = stepIndex;
          continue;
        }
        addUpdateToAnimation(stepIndex, animations[id], update.updates);

        if (update.updates['callbacks'] !== undefined) {
          animations[id].callbacks = update.updates['callbacks'];
        }
      }
    }
    return animations;
  };

  this._createLevelObjects = function (processedLevel) {
    this.toggleGroups = {};
    const pixiObjects = {};
    const targets = new Set();
    const toBeToggled = [];
    for (const name in processedLevel) {
      const animation = processedLevel[name];
      const startState = animation.getStartState();
      const pixiObject = pixiComponent._createPixiObject(startState.type, startState);
      pixiObjects[animation.name] = pixiObject;
      if (startState.toggleGroup) {
        pixiObject.toggleGroup = startState.toggleGroup;
        if (!this.toggleGroups[pixiObject.toggleGroup]) this.toggleGroups[pixiObject.toggleGroup] = [];
        this.toggleGroups[pixiObject.toggleGroup].push(pixiObject);
      }
      if (this.toggleStates.hasOwnProperty(name)) {
        pixiObjects[name].visible = this.toggleStates[name];
      } else if (startState.toggleByDefault) {
        toBeToggled.push(pixiObject);
      }
      if (startState.hoverTargets) startState.hoverTargets.split(',').forEach((n) => targets.add(n));
      if (startState.clickTargets) startState.clickTargets.split(',').forEach((n) => targets.add(n));
      if (startState.interactClickTargets) startState.interactClickTargets.split(',').forEach((n) => targets.add(n));
      if (startState.dragTargets) startState.dragTargets.split(',').forEach((n) => targets.add(n));
      if (this.inputTextFieldStates.hasOwnProperty(name)) {
        pixiObjects[name].setText(this.inputTextFieldStates[name]);
      }
    }
    for (const name of targets)
      if (pixiObjects.hasOwnProperty(name) && !this.toggleStates.hasOwnProperty(name))
        pixiObjects[name].visible = false;
    toBeToggled.forEach((obj) => pixiComponent.toggleObject(obj, true));
    return pixiObjects;
  };

  this._createPixiObject = function (type, gfxUpdate) {
    const startState = gfxUpdate;
    switch (type) {
      case 'polygon':
        return new Polygon(startState);

      case 'rectangle':
        return new Rectangle(startState);

      case 'circle':
        return new Circle(startState, this._pixelsPerUnit);

      case 'bitmap':
        const image = this.getPreloadedImage(startState.image);
        return new Bitmap(startState, image.tex);

      case 'spritesheet':
        const sheetImage = this.getPreloadedImage(startState.image);
        return Spritesheet.fromSheetImage(startState, sheetImage);

      case 'text':
        return Text.createText(startState, this._pixelsPerUnit);

      case 'container':
        return new Container(startState);

      case 'scroll':
        return new ScrollArea(startState);

      case 'html':
        const html = this.preprocessHTMLContent(startState.text);
        return new HTMLArea(startState, this._pixelsPerUnit, html, this.isDarkMode);

      case 'inputtextfield':
        return new InputTextField(startState, this._pixelsPerUnit, this.isDarkMode);

      default:
        return;
    }
  };

  this.preprocessHTMLContent = (html) =>
    html.replaceAll(SRC_REGEX, (_, name) => {
      const image = this.getPreloadedImage(name);
      if (!image.dataURL) {
        let canvas;
        if (image instanceof HTMLCanvasElement) {
          canvas = image;
        } else {
          canvas = document.createElement('canvas');
          canvas.width = image.naturalWidth;
          canvas.height = image.naturalHeight;
          canvas.getContext('2d').drawImage(image, 0, 0);
        }
        image.dataURL = canvas.toDataURL();
      }
      return `src="${image.dataURL}"`;
    });

  function getInterpolatedTrackValue(track, stepIndex, time, interpolate) {
    const start = track.getKeyframeAtHead();
    const end = track.getKeyframeAfterHead();
    const startStep = Math.floor(start[1]);
    const endStep = Math.floor(end[1]);
    if (startStep < stepIndex) {
      // Continue value from previous step from 0.0 on this step.
      start[1] = stepIndex;
    }
    if (stepIndex < endStep) {
      // The next keyframe is in a following step, so no interpolation yet.
      return start[0];
    } else if (start[0] === end[0]) {
      return start[0];
    } else {
      const timeDiff = end[1] - start[1];
      if (timeDiff === 0.0) {
        return end[0];
      } else {
        return interpolate(start[0], end[0], (time - fract(start[1])) / timeDiff);
      }
    }
  }

  this.getPixiObject = function (p) {
    return (p === 'root' && this.stage) || this.pixiObjects[p];
  };

  this.setParent = function (child, parentId) {
    let parent = this.getPixiObject((parentId === undefined && 'root') || parentId);
    parent = parent.content || parent;
    if (Object.is(child.parent, parent)) return;
    if (child.parent) child.parent.removeChild(child);
    parent.addChild(child);
  };

  this.evaluateAnimationTracks = function (pixiObject, trackName, value, saveOldState) {
    if (pixiObject.hasOwnProperty('dragging') && pixiObject.dragging) {
      return;
    }
    let old;
    switch (trackName) {
      case 'color':
        if (pixiObject.widgets) {
          for (let widget of pixiObject.widgets) {
            old = widget.tint;
            widget.tint = value;
          }
        } else {
          old = pixiObject.tint;
          pixiObject.tint = value;
        }
        break;
      case 'scaleX':
        old = pixiObject.scale.x;
        pixiObject.scale.x = value;
        break;
      case 'scaleY':
        old = pixiObject.scale.y;
        pixiObject.scale.y = value;
        break;
      case 'pivotX':
        old = pixiObject.pivot.x;
        pixiObject.pivot.x = value;
        break;
      case 'pivotY':
        old = pixiObject.pivot.y;
        pixiObject.pivot.y = value;
        break;
      case 'parent':
        old = pixiObject.parent;
        this.setParent(pixiObject, value);
        break;
      case 'rotation':
        old = pixiObject.angle;
        pixiObject.angle = value;
        break;
      case 'style':
        old = pixiObject.style;
        pixiObject.style = value;
        break;
      default:
        old = pixiObject[trackName];
        pixiObject[trackName] = value;
        break;
    }
    if (saveOldState && old !== value) {
      if (!pixiObject.oldState) pixiObject.oldState = {};
      pixiObject.oldState[trackName] = old;
    }
  };

  this.applyEffectInternal = function (pixiObject, effects) {
    for (const trackName in effects) {
      if (effects.hasOwnProperty(trackName) && typeof effects[trackName] !== 'undefined') {
        let value = effects[trackName];
        if (trackName === 'color') {
          value = stringColorToNumberColor(value);
        }
        this.evaluateAnimationTracks(pixiObject, trackName, value, true);
      }
    }
  };

  this.applyEffect = function (pixiObject, effects) {
    this.revertEffect(pixiObject);
    this.applyEffectInternal(pixiObject, effects);
  };

  this.revertEffect = function (pixiObject) {
    if (!pixiObject.oldState) return;
    this.applyEffectInternal(pixiObject, pixiObject.oldState);
    delete pixiObject.oldState;
  };

  this.drawLevel = function (stepIndex, time) {
    const levelIndex = this.currentLevel;
    const level = this.processedLevels[levelIndex];

    this.deregisterKeyboardEventListeners();

    const isLastStep = stepIndex === this.levelStepCounts[levelIndex] - 2;
    const isInteract = isLastStep && (commandHandler || restartCommandHandler);
    const isActiveInteract = isInteract && commandHandler;

    function affectedByParent(pixiObject) {
      if (pixiObject.isStandalone) return false;
      const parent = pixiObject.parent;
      if (!parent || parent.isViewArea) return false;
      if (parent.hoverTargets || parent.clickTargets || parent.interactClickTargets) return true;
      if (parent.hoverEffect || parent.actionEffect) return true;
      if (isInteract) {
        if (parent.draggable) return true;
        const animation = level[parent.name];
        if (animation && animation.callbacks && animation.callbacks.includes('click')) return true;
      }
      return affectedByParent(parent);
    }

    function mouseOver(pixiObject, hasAction) {
      if (hasAction && pixiObject.actionEffect) pixiComponent.applyEffect(pixiObject, pixiObject.actionEffect);
      else if (pixiObject.hoverEffect) pixiComponent.applyEffect(pixiObject, pixiObject.hoverEffect);
      if (pixiObject.hoverTargets) pixiComponent.toggleTargets(pixiObject.hoverTargets, true);
      if (pixiObject.children && !pixiObject.isViewArea)
        pixiObject.children.forEach((child) => {
          if (child.isStandalone) return;
          mouseOver(child, hasAction);
        });
    }

    function mouseOut(pixiObject) {
      pixiComponent.revertEffect(pixiObject);
      if (pixiObject.hoverTargets) pixiComponent.toggleTargets(pixiObject.hoverTargets, false);
      if (pixiObject.children && !pixiObject.isViewArea)
        pixiObject.children.forEach((child) => {
          if (child.isStandalone) return;
          mouseOut(child);
        });
    }

    const viewAreasToUpdate = [];
    for (const name in level) {
      const pixiObject = this.pixiObjects[name];
      const animation = level[name];
      if (
        stepIndex < animation.creationStep ||
        (animation.destructionStep !== null && animation.destructionStep <= stepIndex)
      ) {
        if (pixiObject.parent) {
          pixiObject.parent.removeChild(pixiObject);
        }
        continue;
      }
      if (animation.frames) {
        let frameTarget = 0;
        while (frameTarget < animation.frames.length - 1 && animation.frames[frameTarget][1] < time) frameTarget++;
        pixiObject.gotoAndStop(frameTarget);
      }
      animation.trackNames.forEach(function (trackName) {
        const track = animation[trackName];
        let value;
        track.seek(stepIndex + time);
        if (interpolatedTrackNames.includes(trackName)) {
          let interpolate = lerp;
          if (trackName === 'color') {
            interpolate = colorInterpolator(interpolate);
          }
          value = getInterpolatedTrackValue(track, stepIndex, time, interpolate);
        } else {
          value = track.getKeyframeValueAtHead();
        }
        if (['bitmap', 'spritesheet'].includes(animation.type)) {
          if (trackName === 'scaleX') {
            value = (value * animation.width) / pixiObject.texture.width;
          } else if (trackName === 'scaleY') {
            value = (value * animation.height) / pixiObject.texture.height;
          }
        }
        console.assert(
          !(typeof value === 'number' && isNaN(value)),
          'Animation track ' + name + '.' + trackName + ' resulted in NaN.'
        );
        pixiComponent.evaluateAnimationTracks(pixiObject, trackName, value, false);
      });

      pixiObject.eventMode = 'auto';
      pixiObject.cursor = 'default';
      pixiObject.onpointertap = null;
      pixiObject.onpointerover = null;
      pixiObject.onpointerout = null;
      pixiObject.onpointerdown = null;
      pixiObject.onglobalpointermove = null;
      pixiObject.onpointerup = null;
      pixiObject.onpointerupoutside = null;

      let hasAction = false;
      let clickActions = [];

      if (isInteract) {
        if (pixiObject.draggable) {
          if (isActiveInteract) {
            hasAction = true;
            pixiObject.cursor = 'grab';
            setMouseEventsForDrag(pixiObject, this._pixelsPerUnit);
          }
        } else {
          if (animation.callbacks && animation.callbacks.includes('click')) {
            clickActions.push(pixiComponent.onClick);
          }
          if (animation.callbacks && animation.callbacks.includes('submit') && isActiveInteract) {
            if (pixiObject.hasOwnProperty('onsubmitted')) {
              if (!pixiObject.onsubmitted) pixiObject.onsubmitted = onSubmitted;
              pixiObject.isActiveInteract = isActiveInteract;
            }
            if (pixiObject.hasOwnProperty('onmouseevents')) {
              if (!pixiObject.onmouseevents)
                pixiObject.onmouseevents = function (objName, currentText, event) {
                  switch (event.type) {
                    case 'focus':
                      window.removeEventListener('keyup', handleKeyInput);
                      break;
                    case 'blur':
                      window.addEventListener('keyup', handleKeyInput, false);
                      pixiComponent.inputTextFieldStates[pixiObject.name] = currentText;
                      break;
                    default:
                      break;
                  }
                };
            }
          }
          if (pixiObject.interactClickTargets) {
            clickActions.push(() => pixiComponent.toggleTargets(pixiObject.interactClickTargets));
          }
        }

        if (
          animation.callbacks &&
          animation.callbacks.includes('keyinput') &&
          !this.keyboardEventListeners.has(pixiObject.name)
        ) {
          this.keyboardEventListeners.set(pixiObject.name, pixiObject.keyList);
        }
      }
      if (!commandHandler) {
        if (animation.callbacks && animation.callbacks.includes('submit')) {
          pixiObject.isActiveInteract = false;
          delete this.inputTextFieldStates[pixiObject.name];
        }
      }
      if (!pixiObject.draggable && pixiObject.clickTargets) {
        clickActions.push(() => pixiComponent.toggleTargets(pixiObject.clickTargets));
      }

      if (clickActions.length) {
        hasAction = true;
        pixiObject.cursor = 'pointer';
        pixiObject.onpointertap = function (event) {
          clickActions.forEach((action) => action(event, pixiObject));
        };
      }

      const hasHover = pixiObject.hoverEffect || pixiObject.actionEffect || pixiObject.hoverTargets;

      if (hasAction || (hasHover && !affectedByParent(pixiObject))) {
        pixiObject.eventMode = 'static';
        pixiObject.onpointerover = function () {
          mouseOver(pixiObject, hasAction);
        };
        pixiObject.onpointerout = function () {
          mouseOut(pixiObject);
        };
      }

      if (pixiObject.isViewArea) {
        viewAreasToUpdate.push(pixiObject);
        pixiObject.eventMode = 'static';
        pixiObject.onwheel = function (event) {
          adjustScroll(pixiObject, event.deltaX, event.deltaY);
        };
        pixiObject.xThumb.eventMode = 'static';
        pixiObject.xThumb.onpointerdown = function () {
          pixiObject.xThumb.onglobalpointermove = function (event) {
            const pos = event.getLocalPosition(pixiObject.mask).x - pixiObject.xThumb.width / 2;
            const scroll = pixiObject.content.width * (pos / pixiObject.mask.width + 0.5);
            setScroll(pixiObject, scroll, null);
          };
          pixiObject.xThumb.onpointerup = pixiObject.xThumb.onpointerupoutside = function () {
            pixiObject.xThumb.onglobalpointermove = null;
            pixiObject.xThumb.onpointerup = null;
            pixiObject.xThumb.onpointerupoutside = null;
          };
        };
        pixiObject.yThumb.eventMode = 'static';
        pixiObject.yThumb.onpointerdown = function () {
          pixiObject.yThumb.onglobalpointermove = function (event) {
            const pos = event.getLocalPosition(pixiObject.mask).y - pixiObject.yThumb.height / 2;
            const scroll = pixiObject.content.height * (pos / pixiObject.mask.height + 0.5);
            setScroll(pixiObject, null, scroll);
          };
          pixiObject.yThumb.onpointerup = pixiObject.yThumb.onpointerupoutside = function () {
            pixiObject.yThumb.onglobalpointermove = null;
            pixiObject.yThumb.onpointerup = null;
            pixiObject.yThumb.onpointerupoutside = null;
          };
        };
      }
    }

    if (isActiveInteract && this.keyboardEventListeners.size > 0)
      window.addEventListener('keyup', handleKeyInput, false);

    if (pixiComponent.childToReRender) {
      // Move the dragged object in front of other objects with same z_index
      const pixiObject = this.getPixiObject(pixiComponent.childToReRender);
      const prnt = pixiObject.parent;
      prnt.removeChild(pixiObject);
      prnt.addChild(pixiObject);
      pixiComponent.childToReRender = null;
    }

    viewAreasToUpdate.forEach(this.fixScroll);

    if (isInteract) this.replayRestartEvent();
  };

  this.setLevelAndStep = function (level, step) {
    if (this.currentLevel !== level) {
      this.stage.removeChildren();
      this.resetObjectStates();
      this.loadLevelPixiObjects(level);
    }
    this.currentLevel = level;
    this.currentStep = step;
  };

  const stringColorToNumberColor = (function (self) {
    // This canvas is used to convert CSS color values (hex, rgb or name).
    // There is unfortunately no more direct way to query the CSS engine.
    const ctx = document.createElement('canvas').getContext('2d');
    return (color) => {
      if (typeof color !== 'string') return color;
      const parts = color.split('|', 2);
      ctx.fillStyle = self.isDarkMode && parts.length > 1 ? parts[1] : parts[0];
      return parseInt(ctx.fillStyle.substring(1), 16);
    };
  })(this);

  this.deregisterKeyboardEventListeners = function () {
    window.removeEventListener('keyup', handleKeyInput);
    this.keyboardEventListeners.clear();
  };

  let commandHandler = null;
  this.setCommandHandler = function (handler) {
    commandHandler = handler;
  };

  let restartCommandHandler = null;
  this.setRestartCommandHandler = function (handler) {
    restartCommandHandler = handler;
  };

  const handleKeyInput = (event) => {
    if (!commandHandler) return;
    for (const [name, keyList] of this.keyboardEventListeners) {
      // check if the pixiObject accepts the event key
      if (!keyList || keyList === '' || keyList.split(',').includes(event.key)) {
        commandHandler({
          type: 'callback',
          name: name,
          action: 'keyinput',
          params: { key: event.key },
        });
        break;
      }
    }
  };

  this.onClick = function (event, pixiObject) {
    if (commandHandler)
      commandHandler({
        type: 'callback',
        name: pixiObject.name,
        action: 'click',
      });
    else if (restartCommandHandler) {
      const canvasRect = pixiComponent.app.view.getBoundingClientRect();
      pixiComponent.restartClickPoint = {
        normalizedX: (event.clientX - canvasRect.x) / canvasRect.width,
        normalizedY: (event.clientY - canvasRect.y) / canvasRect.height,
      };
      restartCommandHandler();
    }
  };

  this.hasRestartEvent = function () {
    return !!this.restartClickPoint;
  };

  this.replayRestartEvent = function () {
    if (!this.restartClickPoint) return;
    const canvasRect = this.app.view.getBoundingClientRect();
    const x = canvasRect.x + canvasRect.width * this.restartClickPoint.normalizedX;
    const y = canvasRect.y + canvasRect.height * this.restartClickPoint.normalizedY;
    this.restartClickPoint = null;
    const eventInitDict = {
      view: window,
      bubbles: true,
      cancelable: true,
      button: 0,
      buttons: 1,
      isPrimary: true,
      pointerType: 'mouse',
      clientX: x,
      clientY: y,
    };
    this.app.view.dispatchEvent(new PointerEvent('pointerdown', eventInitDict));
    this.app.view.dispatchEvent(new PointerEvent('pointerup', eventInitDict));
  };

  function calculateViewValues(viewArea) {
    const maxScrollX = viewArea.content.width - viewArea.mask.width;
    const maxScrollY = viewArea.content.height - viewArea.mask.height;
    const contentPoint = viewArea.content.getLocalBounds();
    const maskPoint = viewArea.content.toLocal(viewArea.mask.getLocalBounds(), viewArea.mask);
    const scrollX = maskPoint.x - contentPoint.x;
    const scrollY = maskPoint.y - contentPoint.y;
    return {
      x: {
        totalSize: viewArea.content.width,
        viewSize: viewArea.mask.width,
        maxScroll: maxScrollX < 0 ? 0 : maxScrollX,
        currentScroll: scrollX,
        offset: viewArea.content.x + scrollX,
      },
      y: {
        totalSize: viewArea.content.height,
        viewSize: viewArea.mask.height,
        maxScroll: maxScrollY < 0 ? 0 : maxScrollY,
        currentScroll: scrollY,
        offset: viewArea.content.y + scrollY,
      },
    };
  }

  function updateScrollbars(viewArea) {
    const values = calculateViewValues(viewArea);
    viewArea.xThumb.scale.x = values.x.viewSize / values.x.totalSize;
    viewArea.xThumb.x = values.x.viewSize * (values.x.currentScroll / values.x.totalSize - 0.5);
    viewArea.xThumb.visible = values.x.viewSize < values.x.totalSize;
    viewArea.yThumb.scale.y = values.y.viewSize / values.y.totalSize;
    viewArea.yThumb.y = values.y.viewSize * (values.y.currentScroll / values.y.totalSize - 0.5);
    viewArea.yThumb.visible = values.y.viewSize < values.y.totalSize;
  }

  function adjustScroll(viewArea, deltaX, deltaY) {
    const values = calculateViewValues(viewArea);
    if (deltaX) {
      const newScroll = Math.clamp(values.x.currentScroll + deltaX, 0, values.x.maxScroll);
      viewArea.content.x = values.x.offset - newScroll;
      pixiComponent.scrollXStates[viewArea.name] = newScroll;
    }
    if (deltaY) {
      const newScroll = Math.clamp(values.y.currentScroll + deltaY, 0, values.y.maxScroll);
      viewArea.content.y = values.y.offset - newScroll;
      pixiComponent.scrollYStates[viewArea.name] =
        viewArea.followBottom && newScroll === values.y.maxScroll ? 'max' : newScroll;
    }
    updateScrollbars(viewArea);
  }

  function setScroll(viewArea, scrollX, scrollY) {
    const values = calculateViewValues(viewArea);
    if (scrollX || scrollX === 0) {
      const newScroll = Math.clamp(scrollX, 0, values.x.maxScroll);
      viewArea.content.x = values.x.offset - newScroll;
      pixiComponent.scrollXStates[viewArea.name] = newScroll;
    }
    if (scrollY || scrollY === 0) {
      const newScroll = scrollY === 'max' ? values.y.maxScroll : Math.clamp(scrollY, 0, values.y.maxScroll);
      viewArea.content.y = values.y.offset - newScroll;
      pixiComponent.scrollYStates[viewArea.name] =
        viewArea.followBottom && newScroll === values.y.maxScroll ? 'max' : newScroll;
    }
    updateScrollbars(viewArea);
  }

  this.fixScroll = function (viewArea) {
    const values = calculateViewValues(viewArea);
    let scrollX = pixiComponent.scrollXStates[viewArea.name] ?? values.x.currentScroll;
    let scrollY = pixiComponent.scrollYStates[viewArea.name] ?? values.y.currentScroll;
    setScroll(viewArea, scrollX, scrollY);
  };

  this.toggleObject = function (pixiObject, value) {
    if (pixiObject.toggleGroup) {
      if (value === false) return;
      this.setToggleState(pixiObject, true);
      this.toggleGroups[pixiObject.toggleGroup].forEach((obj) => {
        if (obj !== pixiObject) this.setToggleState(obj, false);
      });
      return;
    }
    if (typeof value !== 'boolean') {
      value = !pixiObject.visible;
    }
    this.setToggleState(pixiObject, value);
  };

  this.toggleTargets = function (targets, value) {
    targets.split(',').forEach((name) => this.toggleObject(this.pixiObjects[name], value));
  };

  this.setToggleState = function (pixiObject, value) {
    this.toggleStates[pixiObject.name] = value;
    pixiObject.visible = value;
  };

  this.setInputTextField = function (pixiObject, value) {
    this.inputTextFieldStates[pixiObject.name] = value;
  };

  this.resetObjectStates = function () {
    this.toggleStates = {};
    this.scrollXStates = {};
    this.scrollYStates = {};
    this.inputTextFieldStates = {};
  };

  function onPointerUp(objName, position) {
    // callback event on dropped
    if (!commandHandler) return;
    pixiComponent.childToReRender = objName; // Mark the object to bring in front
    commandHandler({
      type: 'callback',
      name: objName,
      action: 'drop',
      params: { position: position },
    });
  }

  function onSubmitted(objName, input_text) {
    const callbackProps = {
      type: 'callback',
      name: objName,
      action: 'submit',
      params: input_text,
    };
    pixiComponent.inputTextFieldStates[objName] = input_text;
    if (commandHandler) {
      commandHandler(callbackProps);
    } else if (restartCommandHandler) {
      restartCommandHandler(callbackProps);
    }
  }
}
